import React, { useState, useEffect } from 'react';
import { 
  Key, 
  Shield, 
  ShieldCheck, 
  Check, 
  AlertCircle, 
  Zap, 
  Cpu, 
  Brain, 
  Globe, 
  Layers, 
  Search, 
  Sparkles, 
  Trash2, 
  ExternalLink, 
  Copy, 
  Eye, 
  EyeOff, 
  RotateCcw, 
  ArrowRight, 
  ChevronRight, 
  CheckCircle2, 
  X,
  Sliders,
  DollarSign,
  TrendingUp,
  Activity,
  Download,
  Upload,
  Radio
} from 'lucide-react';
import { 
  AiProviderId, 
  AI_PROVIDERS_CONFIG, 
  loadByokVault, 
  saveByokVault, 
  testProviderConnection, 
  purgeAllByokKeys,
  analyzeApiKeySafety,
  ByokVaultState 
} from '../utils/byokService';

interface MultiProviderBYOKModalProps {
  isOpen: boolean;
  onClose: () => void;
  showToast: (msg: string, type?: 'success' | 'info' | 'error') => void;
  initialProvider?: AiProviderId;
}

export function MultiProviderBYOKModal({
  isOpen,
  onClose,
  showToast,
  initialProvider
}: MultiProviderBYOKModalProps) {
  const [vault, setVault] = useState<ByokVaultState>(() => loadByokVault());
  const [selectedTab, setSelectedTab] = useState<AiProviderId>(initialProvider || vault.activeProvider || 'gemini');
  const [showSecretKeys, setShowSecretKeys] = useState<Record<string, boolean>>({});
  const [testingProviderId, setTestingProviderId] = useState<AiProviderId | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [exportJsonString, setExportJsonString] = useState<string | null>(null);

  // Sync state when modal opens or initialProvider changes
  useEffect(() => {
    if (isOpen) {
      const currentVault = loadByokVault();
      setVault(currentVault);
      if (initialProvider) {
        setSelectedTab(initialProvider);
      } else if (currentVault.activeProvider) {
        setSelectedTab(currentVault.activeProvider);
      }
    }
  }, [isOpen, initialProvider]);

  if (!isOpen) return null;

  const currentConfig = AI_PROVIDERS_CONFIG[selectedTab];
  const currentProviderState = vault.providers[selectedTab] || {
    apiKey: '',
    selectedModel: currentConfig.defaultModel,
    status: 'unconfigured'
  };

  const handleKeyChange = (providerId: AiProviderId, val: string) => {
    const updated: ByokVaultState = {
      ...vault,
      providers: {
        ...vault.providers,
        [providerId]: {
          ...vault.providers[providerId],
          apiKey: val,
          status: val.trim().length >= 6 ? 'unconfigured' : 'unconfigured',
          errorMessage: undefined
        }
      }
    };
    setVault(updated);
    saveByokVault(updated);
  };

  const handleModelChange = (providerId: AiProviderId, modelId: string) => {
    const updated: ByokVaultState = {
      ...vault,
      providers: {
        ...vault.providers,
        [providerId]: {
          ...vault.providers[providerId],
          selectedModel: modelId
        }
      }
    };
    setVault(updated);
    saveByokVault(updated);
    showToast(`Default model for ${AI_PROVIDERS_CONFIG[providerId].name} updated to ${modelId}`, 'info');
  };

  const handleSetActiveProvider = (providerId: AiProviderId) => {
    const updated: ByokVaultState = {
      ...vault,
      activeProvider: providerId
    };
    setVault(updated);
    saveByokVault(updated);
    showToast(`Active primary AI provider set to ${AI_PROVIDERS_CONFIG[providerId].name}`, 'success');
  };

  const handleTestKey = async (providerId: AiProviderId) => {
    const pState = vault.providers[providerId];
    if (!pState?.apiKey || pState.apiKey.trim().length < 6) {
      showToast(`Please enter a valid API key for ${AI_PROVIDERS_CONFIG[providerId].name} first.`, 'error');
      return;
    }

    setTestingProviderId(providerId);
    
    // Set testing state in UI
    const testingVault: ByokVaultState = {
      ...vault,
      providers: {
        ...vault.providers,
        [providerId]: {
          ...vault.providers[providerId],
          status: 'testing',
          errorMessage: undefined
        }
      }
    };
    setVault(testingVault);

    const testRes = await testProviderConnection(providerId, pState.apiKey, pState.selectedModel);
    setTestingProviderId(null);

    const finalVault: ByokVaultState = {
      ...vault,
      providers: {
        ...vault.providers,
        [providerId]: {
          ...vault.providers[providerId],
          status: testRes.success ? 'verified' : 'error',
          latencyMs: testRes.latencyMs,
          lastVerifiedAt: testRes.success ? new Date().toISOString() : undefined,
          errorMessage: testRes.success ? undefined : (testRes.error || testRes.message)
        }
      }
    };

    setVault(finalVault);
    saveByokVault(finalVault);

    if (testRes.success) {
      showToast(`Verified! Connected to ${AI_PROVIDERS_CONFIG[providerId].name} (${testRes.latencyMs}ms)`, 'success');
    } else {
      showToast(testRes.message || 'Connection test failed.', 'error');
    }
  };

  const handleToggleAutoFailover = () => {
    const updated: ByokVaultState = {
      ...vault,
      autoFailoverEnabled: !vault.autoFailoverEnabled
    };
    setVault(updated);
    saveByokVault(updated);
    showToast(`Auto-Failover Protection ${updated.autoFailoverEnabled ? 'Enabled' : 'Disabled'}`, 'info');
  };

  const handlePurgeAll = () => {
    purgeAllByokKeys();
    setVault(loadByokVault());
    setShowPurgeConfirm(false);
    showToast('All BYOK keys have been permanently wiped from this browser.', 'info');
  };

  const getProviderIcon = (providerId: AiProviderId, size = 18) => {
    switch (providerId) {
      case 'gemini': return <Sparkles size={size} className="text-blue-500" />;
      case 'groq': return <Zap size={size} className="text-orange-500" />;
      case 'openai': return <Cpu size={size} className="text-emerald-500" />;
      case 'anthropic': return <Brain size={size} className="text-amber-600" />;
      case 'deepseek': return <Layers size={size} className="text-indigo-500" />;
      case 'openrouter': return <Globe size={size} className="text-violet-500" />;
      case 'mistral': return <Shield size={size} className="text-amber-500" />;
      case 'perplexity': return <Search size={size} className="text-teal-500" />;
      default: return <Key size={size} className="text-slate-400" />;
    }
  };

  const connectedCount = Object.entries(vault.providers).filter(([_, p]) => p.apiKey && p.apiKey.trim().length >= 6).length;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-3 sm:p-4 overflow-y-auto animate-in fade-in duration-200">
      <div className="relative w-full max-w-4xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[92vh]">
        
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white tracking-tight">Multi-Provider Sovereign BYOK Vault</h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                  Zero-Custody AES
                </span>
              </div>
              <p className="text-xs text-slate-300">
                Connect your personal AI provider keys. Stored 100% locally in browser memory with zero server logging.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            title="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Telemetry & Quick Status Bar */}
        <div className="px-6 py-3 bg-slate-50 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span className="font-medium">Connected Keys:</span>
              <span className="font-bold text-slate-900 dark:text-white bg-slate-200 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                {connectedCount} of 8 Active
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              <span className="font-medium">Direct Compute Savings:</span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                ${vault.telemetry?.estimatedCostSavedUsd?.toFixed(2) || '0.00'} saved
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleAutoFailover}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all border ${
                vault.autoFailoverEnabled
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-300 dark:border-slate-700'
              }`}
              title="When primary provider hits rate limit or 503, automatically cascade to secondary keys"
            >
              <Activity size={13} className={vault.autoFailoverEnabled ? 'text-emerald-500' : 'text-slate-400'} />
              Auto-Failover: {vault.autoFailoverEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>

        {/* Main Content Area: Left Provider Selector, Right Configuration Card */}
        <div className="flex-1 overflow-y-auto flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-slate-800">
          
          {/* Left Column: Provider List */}
          <div className="w-full md:w-72 p-3 space-y-1.5 bg-slate-50/50 dark:bg-slate-900/50 shrink-0">
            <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Supported AI Providers
            </div>

            {(Object.keys(AI_PROVIDERS_CONFIG) as AiProviderId[]).map((pid) => {
              const pConfig = AI_PROVIDERS_CONFIG[pid];
              const pState = vault.providers[pid];
              const isSelected = selectedTab === pid;
              const isActivePrimary = vault.activeProvider === pid;
              const isConfigured = Boolean(pState?.apiKey && pState.apiKey.trim().length >= 6);
              const isVerified = pState?.status === 'verified';

              return (
                <button
                  key={pid}
                  onClick={() => setSelectedTab(pid)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-medium transition-all flex items-center justify-between ${
                    isSelected
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                      : 'hover:bg-slate-200/70 dark:hover:bg-slate-800/70 text-slate-700 dark:text-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>
                      {getProviderIcon(pid, 16)}
                    </div>
                    <div className="truncate">
                      <div className="font-semibold truncate">{pConfig.name.split(' ')[0]} {pConfig.name.split(' ')[1] || ''}</div>
                      <div className={`text-[10px] truncate ${isSelected ? 'text-indigo-100' : 'text-slate-400'}`}>
                        {pConfig.defaultModel.split('-')[0]}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {isActivePrimary && (
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                        isSelected ? 'bg-white text-indigo-700' : 'bg-indigo-100 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-800'
                      }`}>
                        Primary
                      </span>
                    )}
                    {isVerified ? (
                      <span title="Verified" className={`w-2 h-2 rounded-full bg-emerald-500 ring-2 ${isSelected ? 'ring-white/40' : 'ring-emerald-500/20'}`} />
                    ) : isConfigured ? (
                      <span title="Configured" className="w-2 h-2 rounded-full bg-amber-400" />
                    ) : (
                      <span title="Not configured" className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                    )}
                  </div>
                </button>
              );
            })}

            {/* Quick Links & Free Tier Tip */}
            <div className="pt-4 px-2 space-y-2">
              <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 text-[11px] text-indigo-900 dark:text-indigo-200">
                <div className="font-bold flex items-center gap-1 text-indigo-600 dark:text-indigo-400 mb-1">
                  <Sparkles size={12} /> 100% Free AI Compute
                </div>
                Google AI Studio provides <strong>1,500 requests/day</strong> and Groq offers instant LPU execution with zero credit card needed.
              </div>
            </div>
          </div>

          {/* Right Column: Selected Provider Config Details */}
          <div className="flex-1 p-6 space-y-6">
            
            {/* Top Provider Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800">
                  {getProviderIcon(selectedTab, 24)}
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    {currentConfig.name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {currentConfig.freeTierNote}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={currentConfig.portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors border border-slate-200 dark:border-slate-700"
                >
                  <span>Get API Key</span>
                  <ExternalLink size={12} />
                </a>

                {vault.activeProvider !== selectedTab && (
                  <button
                    onClick={() => handleSetActiveProvider(selectedTab)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-colors"
                  >
                    <Check size={13} />
                    Set as Primary
                  </button>
                )}
              </div>
            </div>

            {/* API Key Input Form */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                  <Key size={13} className="text-indigo-500" />
                  Personal API Key
                </label>
                <div className="text-[11px] text-slate-400">
                  {currentProviderState.lastVerifiedAt ? (
                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 size={12} /> Last verified: {new Date(currentProviderState.lastVerifiedAt).toLocaleTimeString()}
                      {currentProviderState.latencyMs && ` (${currentProviderState.latencyMs}ms)`}
                    </span>
                  ) : (
                    'Not yet verified'
                  )}
                </div>
              </div>

              <div className="relative">
                <input
                  type={showSecretKeys[selectedTab] ? 'text' : 'password'}
                  value={currentProviderState.apiKey || ''}
                  onChange={(e) => handleKeyChange(selectedTab, e.target.value)}
                  placeholder={currentConfig.placeholder}
                  className="w-full px-4 py-2.5 pr-24 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-inner"
                />
                
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowSecretKeys({ ...showSecretKeys, [selectedTab]: !showSecretKeys[selectedTab] })}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    title={showSecretKeys[selectedTab] ? 'Hide Key' : 'Show Key'}
                  >
                    {showSecretKeys[selectedTab] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>

                  {currentProviderState.apiKey && (
                    <button
                      type="button"
                      onClick={() => handleKeyChange(selectedTab, '')}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 transition-colors"
                      title="Clear Key"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Error or Status message */}
              {currentProviderState.errorMessage && (
                <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-xs flex items-start gap-2">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Connection Verification Failed</div>
                    <div className="text-[11px] mt-0.5">{currentProviderState.errorMessage}</div>
                  </div>
                </div>
              )}

              {/* Key Security & Blast Radius Assessment Warning */}
              {currentProviderState.apiKey && currentProviderState.apiKey.trim().length >= 6 && (() => {
                const safety = analyzeApiKeySafety(selectedTab, currentProviderState.apiKey);
                const isWarn = safety.severity === 'warning';
                const isSafe = safety.severity === 'safe';

                return (
                  <div className={`p-3.5 rounded-xl border text-xs space-y-1.5 transition-all ${
                    isWarn
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200'
                      : isSafe
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-200'
                      : 'bg-indigo-500/10 border-indigo-500/25 text-indigo-900 dark:text-indigo-200'
                  }`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 font-bold">
                        <Shield size={14} className={isWarn ? 'text-amber-500' : isSafe ? 'text-emerald-500' : 'text-indigo-400'} />
                        <span>{safety.headline}</span>
                      </div>
                      {safety.spendCapDocUrl && (
                        <a
                          href={safety.spendCapDocUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold underline hover:opacity-80 shrink-0"
                        >
                          <span>Spend Cap Console</span>
                          <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                    <p className="text-[11px] leading-relaxed opacity-90">
                      {safety.recommendation}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Model Selection Dropdown & Options */}
            <div className="space-y-3">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                <Sliders size={13} className="text-indigo-500" />
                Target AI Model
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {currentConfig.models.map((m) => {
                  const isSelectedModel = (currentProviderState.selectedModel || currentConfig.defaultModel) === m.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => handleModelChange(selectedTab, m.id)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        isSelectedModel
                          ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/40 ring-1 ring-indigo-500'
                          : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs text-slate-900 dark:text-white truncate">
                          {m.name}
                        </span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.2 rounded ${
                          m.speed === 'Ultra Fast' ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400' :
                          m.speed === 'Reasoning' ? 'bg-purple-100 dark:bg-purple-950 text-purple-600 dark:text-purple-400' :
                          'bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                        }`}>
                          {m.speed}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                        {m.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Test Connection Action Bar */}
            <div className="pt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={() => handleTestKey(selectedTab)}
                disabled={testingProviderId === selectedTab || !currentProviderState.apiKey}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-950 disabled:opacity-40 transition-all shadow-md"
              >
                {testingProviderId === selectedTab ? (
                  <>
                    <RotateCcw size={14} className="animate-spin" />
                    <span>Testing Connection Ping...</span>
                  </>
                ) : (
                  <>
                    <Zap size={14} className="text-amber-400" />
                    <span>Test & Verify Key</span>
                  </>
                )}
              </button>

              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-400">
                  Sovereign privacy guarantee: zero cloud logging
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* Footer: Zero-Custody Guarantee & Wipe Actions */}
        <div className="px-6 py-4 bg-slate-100 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <Shield className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>
              <strong>Zero-Custody Architecture:</strong> API keys are held strictly in your browser. All requests proxy directly to the AI provider.
            </span>
          </div>

          <div className="flex items-center gap-3">
            {showPurgeConfirm ? (
              <div className="flex items-center gap-2 animate-in fade-in">
                <span className="text-rose-600 dark:text-rose-400 font-bold">Purge all keys?</span>
                <button
                  onClick={handlePurgeAll}
                  className="px-2.5 py-1 rounded-lg bg-rose-600 text-white font-bold hover:bg-rose-700 transition-colors text-xs"
                >
                  Yes, Wipe
                </button>
                <button
                  onClick={() => setShowPurgeConfirm(false)}
                  className="px-2.5 py-1 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-300 text-xs"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowPurgeConfirm(true)}
                className="text-slate-400 hover:text-rose-500 font-medium transition-colors flex items-center gap-1"
                title="Wipe all keys from local storage"
              >
                <Trash2 size={13} />
                <span>Purge Vault</span>
              </button>
            )}

            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl font-bold bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-white transition-colors"
            >
              Done
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
